import { useLayoutEffect } from "react";
import { Problem } from "../../learner/types";
import { setItemWithTimestamp, getItemWithExpiry } from "./handleLocalStorage";

export const TTL = 1000 * 60 * 60 * 24 * 7; // 1 week

export const generateAnswerDataKey = (
  studentId: number | string,
  teacherAssignmentId: string,
  pid: string
) => `problemData_${studentId}_${teacherAssignmentId}_${pid}`;

/**
 * Custom hook to manage storing and restoring student's answers upon navigating away from a problem or closing the tab/window, specifically for custom answer forms. The module programmer must opt-in to using this functionality by setting the `useInputAutosave` property to `true` in the problem's data.
 *
 * @param problemLoaded - boolean indicating whether the problem has loaded
 * @param problem - problem object containing the answer data
 * @param storageKey - key for storing the answer data in local storage
 */
export const useAnswerDataLocalStorage = (
  problemLoaded: boolean,
  problem?: Pick<Problem, "unique_string" | "data">,
  storageKey?: string
) => {
  /**
   * Note: useLayoutEffect is used here to ensure that the saveAnswerData function is called before the component unmounts.
   * A useEffect, by contrast, calls the cleanup function after the component is removed from the DOM.
   *
   * Additionally, we add a beforeunload event listener to handle tab/window close.
   */
  useLayoutEffect(() => {
    if (!problemLoaded) return;

    const useInputAutosave = problem?.data?.data?.useInputAutosave;
    if (storageKey && useInputAutosave) {
      restoreMathBlockInputs(storageKey);
    }

    const save = () => {
      const saveAnswerData = problem?.data?.data?.saveAnswerData;
      if (!storageKey) return;

      const questionAnswerData =
        typeof saveAnswerData === "function" ? saveAnswerData() : {};

      // Find all inputs within #mathBlock
      const mathBlock = document.getElementById("mathBlock");
      const mathBlockInputs: Record<
        string,
        string | string[] | number | boolean
      > = {};

      if (mathBlock && useInputAutosave) {
        // Save <input> values
        const inputs = Array.from(mathBlock.querySelectorAll("input"));
        inputs.forEach((input) => {
          const id = input.id;
          if (!id) return;
          if (input.type === "radio") {
            if (input.checked) mathBlockInputs[id] = input.value;
          } else if (input.type === "checkbox") {
            mathBlockInputs[id] = input.checked;
          } else {
            mathBlockInputs[id] = input.value;
          }
        });

        // Save <select> values
        const selects = Array.from(mathBlock.querySelectorAll("select"));
        selects.forEach((select) => {
          const id = select.id;
          if (!id) return;
          if (select.multiple) {
            mathBlockInputs[id] = Array.from(select.selectedOptions).map(
              (opt) => opt.value
            );
          } else {
            mathBlockInputs[id] = select.value;
          }
        });

        // Save <textarea> values
        const textareas = Array.from(mathBlock.querySelectorAll("textarea"));
        textareas.forEach((textarea) => {
          const id = textarea.id;
          if (!id) return;
          mathBlockInputs[id] = textarea.value;
        });

        // Save MathQuill values
        const mathQuillInputs = mathBlock
          ? Array.from(mathBlock.querySelectorAll(".mq-editable-field"))
          : [];
        mathQuillInputs.forEach((mqField) => {
          const id = mqField.id;

          const MQ = (window as any).MQ;
          if (MQ) {
            const mathField = MQ(mqField);
            const value = mathField?.latex();
            if (id && value) {
              mathBlockInputs[id] = value;
            }
          }
        });
      }

      if (
        Object.keys(questionAnswerData).length === 0 &&
        Object.keys(mathBlockInputs).length === 0
      ) {
        return;
      }

      // Merge mathBlockInputs into the answer data
      const dataToSave = {
        questionAnswerData,
        mathBlockInputs,
      };

      saveToLocalStorage(storageKey, dataToSave);
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => save();
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      save();
    };
  }, [problem, storageKey, problemLoaded]);
};

const updateAnswerDataKeys = (key: string) => {
  const keys = localStorage.getItem("problemDataKeys");
  if (keys) {
    const keyArray = JSON.parse(keys);
    if (!keyArray.includes(key)) {
      keyArray.push(key);
      localStorage.setItem("problemDataKeys", JSON.stringify(keyArray));
    }
  } else {
    localStorage.setItem("problemDataKeys", JSON.stringify([key]));
  }
};

/**
 * Specifically for restoring math block inputs from local storage for problems with a custom answer form
 */
function restoreMathBlockInputs(storageKey: string) {
  const stored = getItemWithExpiry(storageKey, TTL);

  if (!stored) return;

  const mathBlockInputs = stored.mathBlockInputs || {};

  const mathBlock = document.getElementById("mathBlock");
  if (!mathBlock) return;

  const MQ = (window as any).MQ;
  Object.entries(mathBlockInputs).forEach(([id, value]) => {
    const el = mathBlock.querySelector(`[id="${CSS.escape(id)}"]`);
    if (!el || !value) return;

    if (el.classList.contains("mq-editable-field") && MQ) {
      const mathField = MQ(el);
      mathField?.latex(value as string);
    } else if (el instanceof HTMLInputElement) {
      if (el.type === "radio") {
        el.checked = el.value === value;
      } else if (el.type === "checkbox") {
        el.checked = Boolean(value);
      } else {
        el.value = value as string;
      }
    } else if (el instanceof HTMLSelectElement) {
      if (el.multiple && Array.isArray(value)) {
        Array.from(el.options).forEach((option) => {
          option.selected = value.includes(option.value);
        });
      } else {
        el.value = value as string;
      }
    } else if (el instanceof HTMLTextAreaElement) {
      el.value = value as string;
    }
  });
}

export const clearAllAnswerDataKeys = () => {
  const keys = localStorage.getItem("problemDataKeys");
  if (keys) {
    const keyArray = JSON.parse(keys);
    keyArray.forEach((key: string) => {
      localStorage.removeItem(key);
    });
    localStorage.removeItem("problemDataKeys");
  }
};

export const clearExpiredAnswerDataKeys = () => {
  const keys = localStorage.getItem("problemDataKeys");
  if (keys) {
    const keyArray = JSON.parse(keys);
    const newKeyArray = keyArray.filter((key: string) => {
      const value = getItemWithExpiry(key, TTL); // removes expired key
      if (value === null) {
        return false;
      }
      return true;
    });
    if (newKeyArray.length) {
      localStorage.setItem("problemDataKeys", JSON.stringify(newKeyArray));
    }
  }
};

export const saveToLocalStorage = (
  key: string,
  value: Record<string, unknown>
) => {
  setItemWithTimestamp(key, value);
  updateAnswerDataKeys(key);
};

export const getAnswerDataFromLocalStorage = (key: string) =>
  getItemWithExpiry(key, TTL);
